import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { getProductionOrderMaterialRequirements } from "../api/productionOrders";

function formatQuantity(value, uom) {
  if (value === null || value === undefined) return "—";
  const qty = Number(value || 0);
  if (!Number.isFinite(qty)) return "0";
  if (uom === "adet") return Math.round(qty).toLocaleString("tr-TR");
  return qty.toLocaleString("tr-TR", { maximumFractionDigits: 3 });
}

function reservedQuantityCell(item) {
  const lots = Array.isArray(item?.reserved_lots) ? item.reserved_lots : [];
  const quantity = formatQuantity(item?.reserved_qty, item?.uom);
  if (!lots.length) return quantity;

  return (
    <Tooltip
      arrow
      placement="top"
      title={
        <div>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>Rezerve edilen partiler</div>
          {lots.map((lot, index) => (
            <div key={`${lot?.lot_no || "lot"}-${index}`}>{lot?.lot_no || "—"}</div>
          ))}
        </div>
      }
    >
      <span style={{ cursor: "help", textDecoration: "underline dotted" }}>{quantity}</span>
    </Tooltip>
  );
}

export default function WorkOrderMaterialsDialog({ workOrder, onClose }) {
  const [requirements, setRequirements] = useState([]);
  const [operationCount, setOperationCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setRequirements([]);
    setOperationCount(0);
    setError("");
    setLoading(true);

    getProductionOrderMaterialRequirements(Number(workOrder?.id))
      .then((data) => {
        if (cancelled) return;
        setRequirements(Array.isArray(data?.requirements) ? data.requirements : []);
        setOperationCount(Number(data?.operation_count || 0));
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.response?.data?.error?.message || "Malzeme ihtiyaçları yüklenemedi");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [workOrder?.id]);

  return (
    <Dialog open onClose={loading ? undefined : onClose} maxWidth="lg" fullWidth PaperProps={{ sx: { borderRadius: 4, overflow: "hidden", boxShadow: "0 24px 70px rgba(15, 23, 42, 0.22)" } }}>
      <DialogTitle sx={{ p: 0 }}>
        <Box sx={{ px: { xs: 2.5, sm: 3.5 }, py: 2.5, color: "#fff", background: "linear-gradient(135deg, #312e81 0%, #4f46e5 56%, #6366f1 100%)" }}>
          <Stack direction="row" alignItems="center" spacing={1.5}><Box sx={{ width: 42, height: 42, borderRadius: 2.25, display: "grid", placeItems: "center", bgcolor: "rgba(255,255,255,0.16)", border: "1px solid rgba(255,255,255,0.24)", fontSize: 19 }}>▤</Box><Box><Typography variant="h6" fontWeight={800}>İş Emri Malzemeleri</Typography><Typography variant="body2" sx={{ opacity: 0.8 }}>Tüm operasyonlar için hesaplanan malzeme ihtiyacını tek ekranda inceleyin.</Typography></Box></Stack>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: { xs: 2, sm: 3 }, bgcolor: "#f7f8fc" }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, p: 2, border: "1px solid #e7eaf3", borderRadius: 3, bgcolor: "#fff" }}>
          <b>İş Emri:</b> {workOrder?.order_no || "—"} {operationCount ? `· ${operationCount} operasyon` : ""}
        </Typography>

        {loading ? <LinearProgress sx={{ mb: 2, borderRadius: 1 }} /> : null}
        {error ? <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert> : null}

        {!loading && !error && !requirements.length ? (
          <Alert severity="info" sx={{ borderRadius: 2 }}>Bu iş emri için hesaplanmış malzeme ihtiyacı bulunamadı.</Alert>
        ) : null}

        {requirements.length ? (
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3, borderColor: "#e1e7f0" }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ "& th": { fontWeight: 800, bgcolor: "#eef2ff", color: "#312e81", whiteSpace: "nowrap" } }}>
                  <TableCell sx={{ fontWeight: 700 }}>Malzeme</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Malzeme Kodu</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Varyant</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Toplam İhtiyaç</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Güncel İhtiyaç</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Rezerve Edilen</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Birim</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {requirements.map((item) => (
                  <TableRow key={`${item.material_id}:${item.material_variant_id ?? 0}:${item.uom}`}>
                    <TableCell>{item.material_name || `Malzeme #${item.material_id}`}</TableCell>
                    <TableCell>{item.material_code || "—"}</TableCell>
                    <TableCell>
                      {item.variant_summary || item.material_variant_display_name ||
                        (item.material_variant_id ? `Varyant #${item.material_variant_id}` : "—")}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      {formatQuantity(item.qty, item.uom)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: "primary.main" }}>
                      {formatQuantity(item.current_qty, item.uom)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: "success.main" }}>
                      {reservedQuantityCell(item)}
                    </TableCell>
                    <TableCell>{item.uom || "adet"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: { xs: 2, sm: 3.5 }, py: 2, bgcolor: "#fff", borderTop: "1px solid #e7eaf3", justifyContent: "space-between" }}>
        <Typography variant="caption" color="text.secondary">Rezerve edilen miktarın üzerine gelerek ilgili lot numaralarını görebilirsiniz.</Typography>
        <Button onClick={onClose} color="inherit">Kapat</Button>
      </DialogActions>
    </Dialog>
  );
}
